import React, { useMemo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { format, parse, isValid } from 'date-fns';

import Icon from '../../../../../shared/_TBComponents/Icon';
import MaxesGoalsTable, {
  OverflowCell, ClampedCell, Dash, AvatarNameCell,
} from './MaxesGoalsTable';
import ProgressBar from '../ProgressBar';
import { withUnit } from '../../../utils/maxesGoalsHelpers';

const ProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ExerciseIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: hidden;
`;

const ExerciseIconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ExerciseIconCell = ({ row, value }) => (
  <ExerciseIconWrapper>
    <ExerciseIconCircle>
      <Icon icon={row.original.exerciseIcon || 'lift'} size={18} color='#1A1A1A' />
    </ExerciseIconCircle>
    <OverflowCell value={value} />
  </ExerciseIconWrapper>
);

ExerciseIconCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      exerciseIcon: PropTypes.string,
    }),
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ExerciseIconCell.defaultProps = {
  value: '',
};

const ProgressCell = ({ row }) => {
  const { goalPct, goalColor } = row.original;
  // A 0% bar looks broken, so show a dash
  if (!goalPct) return <Dash />;
  return (
    <ProgressWrapper>
      <ProgressBar percent={goalPct} color={goalColor} height='10px' />
      <span>{`${goalPct}%`}</span>
    </ProgressWrapper>
  );
};

ProgressCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      goalPct: PropTypes.number,
      goalColor: PropTypes.string,
    }),
  }).isRequired,
};

const CurrentMaxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const ActionsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background: #FFFFFF;
  padding: 6px 12px;
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #F7F7F7;
  }
`;

const ActionsCell = ({ row }) => (
  <ActionsWrapper>
    <ActionButton type='button' onClick={() => row.original.onAddMax(row.original)}>
      <Icon icon='plus' size={12} />
      Add
    </ActionButton>
    <ActionButton type='button' onClick={() => row.original.onViewHistory(row.original)}>
      <Icon icon='chart-line' size={12} />
      History
    </ActionButton>
  </ActionsWrapper>
);

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onAddMax: PropTypes.func,
      onViewHistory: PropTypes.func,
    }),
  }).isRequired,
};

const CurrentMaxCell = ({ row }) => {
  const { value, unitAbbr, isLocked } = row.original;
  return (
    <CurrentMaxWrapper>
      <OverflowCell value={withUnit(value, unitAbbr)} />
      {value && Boolean(isLocked) && <Icon icon='lock' size={12} />}
    </CurrentMaxWrapper>
  );
};

CurrentMaxCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      value: PropTypes.string,
      unitAbbr: PropTypes.string,
      isLocked: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    }),
  }).isRequired,
};

const SetFromCell = ({ row }) => (
  row.original.setFromLabel ? <ClampedCell value={row.original.setFromLabel} /> : <Dash />
);

SetFromCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({ setFromLabel: PropTypes.string }),
  }).isRequired,
};

// Shows the raw date if it can't be read
const DateCell = ({ value }) => {
  const parsed = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;
  if (!parsed || !isValid(parsed)) return <OverflowCell value={value} />;
  return <OverflowCell value={format(parsed, 'd MMMM yyyy')} />;
};

DateCell.propTypes = { value: PropTypes.string };
DateCell.defaultProps = { value: '' };

const sortByLastThenFirst = (rowA, rowB) => {
  const lastCompare = (rowA.original.last || '').localeCompare(rowB.original.last || '');
  if (lastCompare !== 0) return lastCompare;
  return (rowA.original.first || '').localeCompare(rowB.original.first || '');
};

// Rows with no max always go to the bottom
const sortByDateSet = (rowA, rowB, columnId, desc) => {
  const a = rowA.original.dateSet;
  const b = rowB.original.dateSet;
  if (!a && !b) return 0;
  if (!a) return desc ? -1 : 1;
  if (!b) return desc ? 1 : -1;
  return a < b ? -1 : 1;
};

// The ref lets the page highlight rows after Add Maxes
const MaxesTable = forwardRef(({
  rows, mode, isLoading, onAddMax, onViewHistory, emptyMessage,
}, ref) => {
  const tableData = useMemo(
    () => rows.map((row) => ({
      ...row,
      id: mode === 'athlete' ? row.userId : row.exerciseId,
      onAddMax,
      onViewHistory,
    })),
    [rows, mode, onAddMax, onViewHistory],
  );

  const columns = useMemo(() => {
    const primaryColumn = mode === 'athlete'
      ? {
        Header: 'Athlete',
        id: 'athleteName',
        accessor: (row) => `${row.first || ''} ${row.last || ''}`.trim(),
        sortType: sortByLastThenFirst,
        headerStyle: { minWidth: '180px', flex: '1' },
        cellStyle: { minWidth: '180px', flex: '1' },
        Cell: AvatarNameCell,
      }
      : {
        Header: 'Exercise',
        accessor: 'exerciseName',
        headerStyle: { minWidth: '180px', flex: '1' },
        cellStyle: { minWidth: '180px', flex: '1' },
        Cell: ExerciseIconCell,
      };

    return [
      primaryColumn,
      {
        Header: 'Current Max',
        accessor: 'value',
        headerStyle: { width: '160px' },
        Cell: CurrentMaxCell,
      },
      {
        Header: 'Date Set',
        accessor: 'dateSet',
        sortType: sortByDateSet,
        headerStyle: { width: '180px' },
        Cell: DateCell,
      },
      {
        Header: 'Set From',
        id: 'setFrom',
        disableSortBy: true,
        headerStyle: { width: '180px' },
        Cell: SetFromCell,
      },
      {
        Header: 'Goal Progress',
        id: 'goalProgress',
        disableSortBy: true,
        headerStyle: { minWidth: '160px', flex: '1' },
        cellStyle: { minWidth: '160px', flex: '1' },
        Cell: ProgressCell,
      },
      {
        Header: 'Actions',
        id: 'actions',
        sticky: 'right',
        disableSortBy: true,
        headerStyle: { width: '200px', textAlign: 'center' },
        cellStyle: { width: '200px', textAlign: 'center' },
        Cell: ActionsCell,
      },
    ];
  }, [mode]);

  const isAthleteMode = mode === 'athlete';

  return (
    <MaxesGoalsTable
      ref={ref}
      data={tableData}
      columns={columns}
      isLoading={isLoading}
      hideSearch
      emptyIcon='lift'
      entityLabel={isAthleteMode ? 'athlete' : 'exercise'}
      defaultSortBy={[
        { id: 'dateSet', desc: true },
        { id: isAthleteMode ? 'athleteName' : 'exerciseName', desc: false },
      ]}
      emptyMessage={emptyMessage || (isAthleteMode ? 'No athletes found.' : 'No exercises found.')}
    />
  );
});

MaxesTable.displayName = 'MaxesTable';

MaxesTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    exerciseId: PropTypes.number,
    exerciseName: PropTypes.string,
    userId: PropTypes.number,
    first: PropTypes.string,
    last: PropTypes.string,
    pic: PropTypes.string,
    exerciseIcon: PropTypes.string,
    value: PropTypes.string,
    dateSet: PropTypes.string,
    goalPct: PropTypes.number,
    goalColor: PropTypes.string,
    setFromLabel: PropTypes.string,
  })),
  mode: PropTypes.oneOf(['exercise', 'athlete']),
  isLoading: PropTypes.bool,
  onAddMax: PropTypes.func,
  onViewHistory: PropTypes.func,
  emptyMessage: PropTypes.string,
};

MaxesTable.defaultProps = {
  rows: [],
  mode: 'exercise',
  isLoading: false,
  onAddMax: () => {},
  onViewHistory: () => {},
  emptyMessage: null,
};

export default MaxesTable;
